import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MapPin, RefreshCw, X } from 'lucide-react';
import { GoogleBusinessIcon } from './PlatformIcon';
import { Capacitor } from '@capacitor/core';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import {
  formatGbpConnectError,
  type GbpConnectErrorInfo,
} from '../lib/gbpConnectErrors';
import { clearGbpConnectSession, resetGbpConnectState } from '../lib/gbpConnectReset';
import { stripGbpOauthSearch } from '../lib/gbpOauthResume';
import { buildGbpOauthReturnUrl, oauthReturnPath } from '../lib/nativeAppScheme';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import GoogleBusinessConnectErrorModal from './GoogleBusinessConnectErrorModal';
import toast from 'react-hot-toast';

const GBP_CONNECT_BUSINESS_KEY = 'gbp_connect_business_id';
const GBP_CONNECT_RETURN_PATH_KEY = 'gbp_connect_return_path';
/** Set while the Google sign-in is open so we can finalize on return. */
const GBP_GOOGLE_LINKING_KEY = 'gbp_google_linking';

interface GoogleBusinessConnectButtonProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  /** Hide the green/red status dot (e.g. when the parent tab shows it). */
  hideStatusIndicator?: boolean;
  /** Called after connect/disconnect so parent modals can refresh. */
  onConnectionChange?: () => void;
  /** Connect an additional Google location alongside the current one. */
  addLocation?: boolean;
  /** Optional custom label for the connect button. */
  buttonLabel?: string;
  /** Hide the explanatory line under the button (when the parent already explains). */
  hideHelperText?: boolean;
  /**
   * Called instead of the inline picker when the signed-in Google account
   * manages several locations (Integrations shows its own enable/disable list).
   */
  onLocationsReady?: () => void;
  /**
   * Start Google sign-in as soon as the button is ready. Used when Marketing
   * opens this flow from a disconnected Platforms row. Skipped on an OAuth return.
   */
  autoStart?: boolean;
  /**
   * Tag this sign-in so the app reopens the feedback form on return
   * (web and the iOS custom-scheme handoff).
   */
  returnContext?: 'feedback';
}

interface GoogleBusinessConnection {
  id: string;
  location_id: string | null;
  location_name: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  provider?: string | null;
}

interface GbpLocationOption {
  accountId: string;
  locationId: string;
  title: string;
  placeId: string | null;
  address: string | null;
}

export default function GoogleBusinessConnectButton({
  size = 'medium',
  className = '',
  hideStatusIndicator = false,
  onConnectionChange,
  addLocation = false,
  buttonLabel,
  hideHelperText = false,
  onLocationsReady,
  autoStart = false,
  returnContext,
}: GoogleBusinessConnectButtonProps) {
  const { selectedBusiness: business } = useBusiness();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [connection, setConnection] = useState<GoogleBusinessConnection | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [connectError, setConnectError] = useState<GbpConnectErrorInfo | null>(null);
  const [locationOptions, setLocationOptions] = useState<GbpLocationOption[] | null>(null);
  const [selectingLocation, setSelectingLocation] = useState<string | null>(null);
  const autoStartRef = useRef(false);

  const showConnectError = useCallback((info: GbpConnectErrorInfo) => {
    setConnectError(info);
  }, []);

  /** Legacy aggregator link that still needs a one-time Google reconnect. */
  const needsGoogleReconnect = !!connection && connection.provider !== 'google';

  const checkConnection = useCallback(async () => {
    if (!business?.id) {
      setCheckingConnection(false);
      return;
    }
    try {
      const { data: activeRows } = await supabase
        .from('google_business_connections')
        .select('id, location_id, location_name, is_active, last_synced_at, provider')
        .eq('business_id', business.id)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true)
        .order('created_at', { ascending: true })
        .limit(5);
      const rows = activeRows || [];
      setConnection(rows.find((r) => r.provider === 'google') || rows[0] || null);
    } catch (error) {
      console.error('Error checking connection:', error);
    } finally {
      setCheckingConnection(false);
    }
  }, [business?.id]);

  const clearLinkingFlag = () => {
    try {
      sessionStorage.removeItem(GBP_GOOGLE_LINKING_KEY);
    } catch { /* ignore */ }
  };

  const completeConnection = useCallback(async () => {
    clearGbpConnectSession();
    clearLinkingFlag();
    toast.success('Google Business Profile connected!');
    await checkConnection();
    onConnectionChange?.();
  }, [checkConnection, onConnectionChange]);

  /** After Google redirects back, activate the pending connection. */
  const finalizeConnection = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!business?.id) return false;
      setFinalizing(true);
      try {
        const { data, error } = await supabase.functions.invoke('google-gbp-connect', {
          body: { businessId: business.id, action: 'finalize' },
        });
        if (error || data?.error) {
          const msg =
            (await invokeErrorMessage(error, data)) || 'Failed to finish the Google connection.';
          if (!opts?.silent) showConnectError(formatGbpConnectError('sync_failed', msg));
          return false;
        }
        if (data?.needsLocationPick && Array.isArray(data.locations)) {
          clearLinkingFlag();
          clearGbpConnectSession();
          if (onLocationsReady) {
            onLocationsReady();
            return true;
          }
          setLocationOptions(
            (data.locations as Array<GbpLocationOption & { enabled?: boolean }>).filter((l) => !l.enabled),
          );
          return true;
        }
        if (!data?.linked) {
          // User came back without completing the Google sign-in.
          if (!opts?.silent) {
            showConnectError(
              formatGbpConnectError(
                'sync_failed',
                data?.message ||
                  'Google Business Profile was not linked. Click Connect and finish the Google sign-in.',
              ),
            );
          }
          return false;
        }
        await completeConnection();
        return true;
      } catch (e) {
        console.error(e);
        if (!opts?.silent) {
          showConnectError(
            formatGbpConnectError('sync_failed', 'Failed to finalize Google Business connection.'),
          );
        }
        return false;
      } finally {
        setFinalizing(false);
      }
    },
    [business?.id, completeConnection, showConnectError, onLocationsReady],
  );

  const selectLocation = async (loc: GbpLocationOption) => {
    if (!business?.id) return;
    setSelectingLocation(loc.locationId);
    try {
      const { data, error } = await supabase.functions.invoke('google-gbp-connect', {
        body: {
          businessId: business.id,
          action: 'select',
          accountId: loc.accountId,
          locationId: loc.locationId,
          locationName: loc.title,
          placeId: loc.placeId,
        },
      });
      if (error || data?.error) {
        const msg =
          (await invokeErrorMessage(error, data)) || 'Failed to connect that location.';
        showConnectError(formatGbpConnectError('sync_failed', msg));
        return;
      }
      setLocationOptions(null);
      await completeConnection();
    } catch (e) {
      console.error(e);
      showConnectError(
        formatGbpConnectError('sync_failed', 'Failed to connect that location.'),
      );
    } finally {
      setSelectingLocation(null);
    }
  };

  useEffect(() => {
    void checkConnection();
  }, [checkConnection]);

  // Finalize when returning from Google (redirect carries gbp_oauth=1 /
  // gbp_returning=1 / success=google_business_*, or the session flag is set).
  useEffect(() => {
    if (!business?.id || (connection && !needsGoogleReconnect && !addLocation)) return;

    const urlParams = new URLSearchParams(window.location.search);
    const returnedViaUrl =
      urlParams.get('gbp_oauth') === '1' ||
      urlParams.get('gbp_returning') === '1' ||
      (urlParams.get('success') || '').startsWith('google_business_');
    let linkingInProgress = false;
    try {
      linkingInProgress = sessionStorage.getItem(GBP_GOOGLE_LINKING_KEY) === '1';
    } catch { /* ignore */ }

    if (!returnedViaUrl && !linkingInProgress) return;

    const storedBusinessId = (() => {
      try {
        return sessionStorage.getItem(GBP_CONNECT_BUSINESS_KEY) || '';
      } catch {
        return '';
      }
    })();
    if (storedBusinessId && storedBusinessId !== business.id) return;

    void (async () => {
      const ok = await finalizeConnection({ silent: !returnedViaUrl });
      if (ok) {
        const next = stripGbpOauthSearch(window.location.search);
        if (`${window.location.pathname}${window.location.search}` !== `${window.location.pathname}${next}`) {
          navigate({ pathname: window.location.pathname, search: next }, { replace: true });
        }
      } else {
        // A failed return must not stay armed. Otherwise the next visit to
        // any page retries finalize and shows "sync_failed" again.
        clearLinkingFlag();
        clearGbpConnectSession();
        const next = stripGbpOauthSearch(window.location.search);
        if (`${window.location.pathname}${window.location.search}` !== `${window.location.pathname}${next}`) {
          navigate({ pathname: window.location.pathname, search: next }, { replace: true });
        }
      }
    })();
  }, [business?.id, connection, needsGoogleReconnect, addLocation, finalizeConnection, navigate]);

  // Also re-check when the tab regains focus (native browser sheet closed
  // before the redirect fired).
  useEffect(() => {
    if (!business?.id) return;
    const onFocus = () => {
      let linking = false;
      try {
        linking = sessionStorage.getItem(GBP_GOOGLE_LINKING_KEY) === '1';
      } catch { /* ignore */ }
      if (linking && (!connection || needsGoogleReconnect)) {
        void finalizeConnection({ silent: true });
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [business?.id, connection, needsGoogleReconnect, finalizeConnection]);

  const handleConnect = async () => {
    if (!business?.id) return;

    setLoading(true);
    setConnectError(null);
    try {
      await resetGbpConnectState(business.id);

      sessionStorage.setItem(GBP_CONNECT_BUSINESS_KEY, business.id);
      const returnPath = oauthReturnPath();
      sessionStorage.setItem(GBP_CONNECT_RETURN_PATH_KEY, returnPath);
      if (returnContext === 'feedback') {
        try {
          sessionStorage.setItem('feedback_gbp_connect', '1');
        } catch { /* ignore */ }
      }
      const returnUrl = buildGbpOauthReturnUrl({
        nextPath: returnPath,
        ...(returnPath === '/app-store' ? { tab: 'integrations' } : {}),
        ...(returnContext === 'feedback' ? { extraParams: { feedback_connect: '1' } } : {}),
      });

      const { data, error } = await supabase.functions.invoke('google-gbp-connect', {
        body: { businessId: business.id, action: 'start', returnUrl },
      });

      if (error || data?.error) {
        const msg =
          (await invokeErrorMessage(error, data)) ||
          'Failed to start Google Business connect. Please try again.';
        showConnectError(formatGbpConnectError('connect_start_failed', msg));
        return;
      }

      if (!data?.authUrl) {
        showConnectError(
          formatGbpConnectError(
            'connect_start_failed',
            'Could not get a Google sign-in URL. Please try again.',
          ),
        );
        return;
      }

      try {
        sessionStorage.setItem(GBP_GOOGLE_LINKING_KEY, '1');
      } catch { /* ignore */ }

      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.authUrl });
      } else {
        // Google's callback redirects straight back into the app, so a
        // same-tab hand-off is cleaner than a popup that becomes the app.
        window.location.href = data.authUrl;
      }
    } catch (error) {
      console.error('Error connecting to Google Business Profile:', error);
      showConnectError(
        formatGbpConnectError(
          'connect_start_failed',
          'Failed to connect to Google Business Profile. Please try again.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoStart || autoStartRef.current) return;
    if (!business?.id || checkingConnection) return;
    const urlParams = new URLSearchParams(window.location.search);
    const returning =
      urlParams.get('gbp_oauth') === '1' ||
      urlParams.get('gbp_returning') === '1' ||
      (urlParams.get('success') || '').startsWith('google_business_');
    if (returning) return;
    if (connection && !needsGoogleReconnect && !addLocation) return;
    autoStartRef.current = true;
    void handleConnect();
  }, [autoStart, business?.id, checkingConnection, connection, needsGoogleReconnect, addLocation]);

  const sizeClasses = {
    small: 'px-3 py-1.5 text-xs',
    medium: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-base',
  };

  const busy = loading || finalizing;
  const showConnectButton = !connection || needsGoogleReconnect || addLocation;

  return (
    <>
      {checkingConnection ? (
        hideStatusIndicator ? null : (
          <div className={`flex flex-col gap-2 ${className}`}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-gray-300 animate-pulse" aria-hidden />
              <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
            </div>
          </div>
        )
      ) : !showConnectButton ? (
        hideStatusIndicator ? null : (
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full bg-green-500 shrink-0 ${className}`}
            title="Connected"
            aria-label="Google Business Profile connected"
          />
        )
      ) : (
        <div className={`flex flex-col gap-3 ${className}`}>
          <div className="flex flex-wrap items-center gap-3">
            {!hideStatusIndicator && !addLocation && (
              <span
                className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                  needsGoogleReconnect ? 'bg-amber-500' : 'bg-red-500'
                }`}
                title={needsGoogleReconnect ? 'Reconnect required' : 'Not connected'}
                aria-label={
                  needsGoogleReconnect
                    ? 'Google Business Profile needs to be reconnected'
                    : 'Google Business Profile not connected'
                }
              />
            )}
            <button
              type="button"
              onClick={() => void handleConnect()}
              disabled={busy}
              className={`${sizeClasses[size]} flex items-center justify-center gap-2 ${
                needsGoogleReconnect && !addLocation
                  ? 'bg-amber-500 hover:bg-amber-600'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto`}
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{finalizing ? 'Finishing…' : 'Connecting…'}</span>
                </>
              ) : needsGoogleReconnect && !addLocation ? (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>{buttonLabel || 'Reconnect with Google'}</span>
                </>
              ) : (
                <>
                  <GoogleBusinessIcon className="h-5 w-5" />
                  <span>
                    {buttonLabel ||
                      (addLocation
                        ? 'Add another location'
                        : 'Connect Google Business Profile')}
                  </span>
                </>
              )}
            </button>
          </div>
          {busy ? (
            <p className="text-[11px] text-gray-500 leading-tight">
              This may take a moment…
            </p>
          ) : needsGoogleReconnect && !addLocation && !hideHelperText ? (
            <p className="text-[11px] text-amber-700 leading-tight">
              Google Business Profile now connects directly through Google. Sign in once to
              unlock photos, performance insights and profile editing — your posts, replies and
              automation settings carry over.
            </p>
          ) : null}
        </div>
      )}

      {locationOptions ? (
        <div
          className="fixed inset-0 z-[100120] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gbp-location-pick-title"
        >
          <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <div>
                <h3 id="gbp-location-pick-title" className="text-base font-semibold text-gray-900">
                  Which location is this business?
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Your Google account manages {locationOptions.length} Business Profiles.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLocationOptions(null)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
              {locationOptions.map((loc) => {
                const picking = selectingLocation === loc.locationId;
                return (
                  <li key={`${loc.accountId}:${loc.locationId}`}>
                    <button
                      type="button"
                      disabled={!!selectingLocation}
                      onClick={() => void selectLocation(loc)}
                      className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-blue-50 disabled:opacity-60"
                    >
                      <span className="h-9 w-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                        {picking ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MapPin className="h-4 w-4" />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">
                          {loc.title}
                        </span>
                        {loc.address ? (
                          <span className="block text-xs text-gray-500 truncate">{loc.address}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}

      <GoogleBusinessConnectErrorModal
        error={connectError}
        onClose={() => {
          setConnectError(null);
          clearLinkingFlag();
          clearGbpConnectSession();
          const next = stripGbpOauthSearch(window.location.search);
          if (`${window.location.pathname}${window.location.search}` !== `${window.location.pathname}${next}`) {
            navigate({ pathname: window.location.pathname, search: next }, { replace: true });
          }
        }}
        onRetry={() => void handleConnect()}
      />
    </>
  );
}
